import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { ModalComponent, ButtonComponent } from '@comps/components';
import { Icon } from '@iconify/react';
import "../case.css";

/**
 * Shows the 3D setup link inside the site (embedded viewer) instead of
 * sending the user off to another tab.
 *
 * Some external viewers refuse to be framed (X-Frame-Options /
 * frame-ancestors), which JS can not detect, so "Open in new tab" always
 * stays available in the header as an escape hatch.
 */
const View3DSetup = ({ link, onClosed }) => {
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  // Some viewers never fire onLoad (they keep streaming) — drop the spinner
  // anyway so it can not sit on top of a viewer that is already usable.
  useEffect(() => {
    timerRef.current = setTimeout(() => setLoading(false), 8000);
    return () => clearTimeout(timerRef.current);
  }, [link]);

  const onFrameLoad = () => {
    clearTimeout(timerRef.current);
    setLoading(false);
  };

  const openInNewTab = () => window.open(link, "_blank", "noopener,noreferrer");

  const title = (
    <span className="view-3d-title">
      <span className="view-3d-title-text">
        <Icon icon="tabler:cube-3d-sphere" width={ 18 } />
        3D Setup
      </span>
      <span className="view-3d-title-actions">
        <ButtonComponent size="small" className="view-3d-open-btn" onClick={ openInNewTab }>
          <Icon icon="tabler:external-link" width={ 15 } />
          <span className="view-3d-open-label">Open in new tab</span>
        </ButtonComponent>
        <button type="button" className="view-3d-close" onClick={ () => onClosed(false) } aria-label="Close 3D setup">
          <Icon icon="tabler:x" width={ 19 } />
        </button>
      </span>
    </span>
  );

  return <ModalComponent
    open={ true }
    footer={ false }
    closable={ false }
    wrapClassName="view-3d-wrap"
    width={ 'min(1440px, 96vw)' }
    mainTitle={ title }
    onCancel={ () => onClosed(false) }
  >
    <div className="view-3d-body">
      <div className="view-3d-frame-wrap">
        { loading &&
          <div className="view-3d-loader">
            <Spin size="large" tip="Loading 3D setup..." />
          </div>
        }
        <iframe
          className="view-3d-frame"
          title="3D Setup"
          src={ link }
          onLoad={ onFrameLoad }
          allow="fullscreen; xr-spatial-tracking; accelerometer; gyroscope"
          allowFullScreen
        />
      </div>
    </div>
  </ModalComponent>;
};

export default View3DSetup;
